import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { informesApi, usuariosApi, comisionistasApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  FileBarChart, Upload, FileText, LayoutGrid, History, Download, Eye, EyeOff, Loader2,
  AlertTriangle, Trash2, ChevronDown, Search, Package, X, Settings, Link2, CheckCircle2,
} from 'lucide-react';

function descargarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const eur = (n) => (n || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 }) + ' €';


// Distintivo de cada modelo en la biblioteca.
const COLOR_TIPO = {
  A: 'bg-slate-100 text-slate-700',
  B: 'bg-sky-100 text-sky-700',
  C: 'bg-amber-100 text-amber-800',
  _: 'bg-sky-100 text-sky-700',
};

export default function Informes() {
  const { hasPermission } = useAuth();
  const gestor = hasPermission('informes', 'crear');
  const puedeBorrar = hasPermission('informes', 'eliminar');
  const puedeEditar = hasPermission('informes', 'editar');

  const [tab, setTab] = useState(gestor ? 'biblioteca' : 'mis');

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <header className="flex items-center gap-3">
        <FileBarChart className="w-7 h-7 text-sky-700" />
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Informes comerciales</h1>
          <p className="text-sm text-slate-500">
            {gestor ? 'Genera, gestiona y publica los informes por comercial.'
                    : 'Tus informes mensuales publicados por dirección comercial.'}
          </p>
        </div>
      </header>

      {gestor && (
        <div className="flex gap-1 border-b border-slate-200">
          {[['biblioteca', 'Biblioteca'], ['generar', 'Generar'], ['ajustes', 'Ajustes']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg -mb-px border-b-2 ${
                tab === k ? 'text-sky-700 border-sky-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {gestor && tab === 'generar' && <Generar onDone={() => setTab('biblioteca')} />}
      {gestor && tab === 'ajustes' && <Ajustes />}
      {(!gestor || tab === 'biblioteca') && (
        <Biblioteca gestor={gestor} puedeBorrar={puedeBorrar} puedeEditar={puedeEditar} />
      )}
    </div>
  );
}

/* ───────────────────────── GENERAR ───────────────────────── */

const FAMILIAS = [
  { clave: 'zona', label: 'Informe zona mensual' },
  { clave: 'grupos', label: 'Informe mensual grupos' },
  { clave: 'grupos_depositos', label: 'Informe grupo depósitos' },
];

function Generar({ onDone }) {
  const [familia, setFamilia] = useState('zona');
  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tipo de informe</span>
        <div className="flex gap-1 flex-wrap">
          {FAMILIAS.map((f) => (
            <button key={f.clave} onClick={() => setFamilia(f.clave)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${
                familia === f.clave ? 'bg-sky-600 text-white border-sky-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-sky-400'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {familia === 'zona'
        ? <GenerarZona onDone={onDone} />
        : <GenerarGrupos familia={familia} onDone={onDone} />}
    </div>
  );
}

// Cuadre: comprobaciones cruzadas ventas ↔ comisiones. Solo advierte, nunca bloquea.
function PanelCuadre({ cuadre }) {
  const [abierta, setAbierta] = useState(null);
  const fallidas = (cuadre.comprobaciones || []).filter((c) => !c.ok).length;

  return (
    <div className={`border rounded-lg overflow-hidden ${fallidas ? 'border-amber-300' : 'border-emerald-200'}`}>
      <div className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold ${
        fallidas ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
        {fallidas
          ? <><AlertTriangle className="w-4 h-4" /> Cuadre: {fallidas} de {cuadre.comprobaciones.length} comprobaciones con avisos</>
          : <><CheckCircle2 className="w-4 h-4" /> Cuadre: todo correcto ({cuadre.comprobaciones.length} comprobaciones)</>}
      </div>
      <ul className="divide-y divide-slate-100 bg-white">
        {(cuadre.comprobaciones || []).map((c) => (
          <li key={c.clave}>
            <button type="button" onClick={() => setAbierta(abierta === c.clave ? null : c.clave)}
              className="w-full flex items-start gap-2 text-left px-3 py-2 hover:bg-slate-50">
              {c.ok
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />}
              <span className="flex-1 text-sm">
                <span className="font-medium text-slate-700">{c.titulo}</span>
                <span className="block text-xs text-slate-500">{c.resumen}</span>
              </span>
              {c.detalles?.length > 0 && (
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 mt-0.5 transition-transform ${
                  abierta === c.clave ? '' : '-rotate-90'}`} />
              )}
            </button>
            {abierta === c.clave && c.detalles?.length > 0 && (
              <ul className="px-9 pb-3 space-y-1 text-xs text-slate-600">
                {c.detalles.map((d, i) => <li key={i} className="list-disc list-outside ml-3">{d}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function GenerarZona({ onDone }) {
  const [ventasFile, setVentasFile] = useState(null);
  const [comisionesFile, setComisionesFile] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [tipos, setTipos] = useState({ A: true, B: true, C: false });
  const [generando, setGenerando] = useState(false);

  const analizar = async () => {
    if (!ventasFile || !comisionesFile) return toast.error('Sube los dos PDF (ventas y comisiones).');
    setAnalizando(true);
    try {
      const r = await informesApi.analizar(ventasFile, comisionesFile);
      setAnalisis(r);
      setSeleccion(new Set((r.comerciales || []).map((c) => c.codigo)));
      toast.success(`${(r.comerciales || []).length} comerciales detectados.`);
      (r.avisos_fuentes || []).forEach((a) => toast.warning(a));
    } catch (e) { toast.error(e.message); } finally { setAnalizando(false); }
  };

  const toggle = (cod) => setSeleccion((p) => {
    const s = new Set(p); if (s.has(cod)) s.delete(cod); else s.add(cod); return s;
  });
  const todos = analisis && seleccion.size === (analisis.comerciales || []).length;

  const generar = async () => {
    const tiposSel = Object.entries(tipos).filter(([, v]) => v).map(([k]) => k);
    if (!seleccion.size) return toast.error('Selecciona al menos un comercial.');
    if (!tiposSel.length) return toast.error('Selecciona Documento A y/o B.');
    setGenerando(true);
    try {
      const r = await informesApi.generar(analisis.job_id, tiposSel, [...seleccion]);
      toast.success(`${r.total} informes generados.`);
      (r.avisos || []).forEach((a) => toast.warning(a));
      onDone();
    } catch (e) { toast.error(e.message); } finally { setGenerando(false); }
  };

  return (
    <div className="space-y-5">
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-sky-700">1 · Subir datos del ERP (PDF)</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <FileDrop label="Ventas mensual por cliente" file={ventasFile} onChange={setVentasFile} />
          <FileDrop label="Informe de comisiones" file={comisionesFile} onChange={setComisionesFile} />
        </div>
        <Button onClick={analizar} disabled={analizando} className="gap-2">
          {analizando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Analizar
        </Button>
      </section>

      {analisis && (
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-sky-700">2 · Seleccionar y generar</h2>
            <span className="text-xs text-slate-500">Comisiones: {analisis.periodo_comisiones} · Ventas: {analisis.periodo_ventas}</span>
          </div>
          {!analisis.ventas_ok && (analisis.ventas_avisos || []).map((a, i) => (
            <div key={i} className="flex gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {a}
            </div>
          ))}
          {analisis.cuadre && <PanelCuadre cuadre={analisis.cuadre} />}
          <div className="overflow-x-auto border border-slate-100 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="p-2 w-10 text-left"><input type="checkbox" checked={!!todos}
                    onChange={() => setSeleccion(todos ? new Set() : new Set(analisis.comerciales.map((c) => c.codigo)))} /></th>
                  <th className="p-2 text-left">Comercial</th>
                  <th className="p-2 text-left">Zona CRM</th>
                  <th className="p-2 text-left">Destinatario</th>
                  <th className="p-2 text-right">Ventas</th>
                </tr>
              </thead>
              <tbody>
                {analisis.comerciales.map((c) => (
                  <React.Fragment key={c.codigo}>
                    <tr className={`border-t border-slate-100 ${c.avisos_cuadre?.length ? 'bg-amber-50/50' : ''}`}>
                      <td className="p-2"><input type="checkbox" checked={seleccion.has(c.codigo)} onChange={() => toggle(c.codigo)} /></td>
                      <td className="p-2"><span className="text-slate-400">{c.codigo}</span> {c.nombre}</td>
                      <td className="p-2">{c.zona_crm || <span className="text-slate-400">—</span>}</td>
                      <td className="p-2">{c.destinatario_id
                        ? <span className="text-emerald-600">✓ {c.destinatario_origen === 'manual' ? 'enlace manual' : 'titular zona'}</span>
                        : <span className="text-amber-600">sin destinatario</span>}</td>
                      <td className="p-2 text-right tabular-nums">{eur(c.total_ventas)}</td>
                    </tr>
                    {c.avisos_cuadre?.length > 0 && (
                      <tr className="bg-amber-50/50">
                        <td />
                        <td colSpan={4} className="px-2 pb-2">
                          <ul className="text-xs text-amber-800 space-y-0.5">
                            {c.avisos_cuadre.map((a, i) => (
                              <li key={i} className="flex gap-1.5">
                                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {a}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={tipos.A}
              onChange={(e) => setTipos((t) => ({ ...t, A: e.target.checked }))} /><FileText className="w-4 h-4 text-slate-500" /> Documento A (fiel)</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={tipos.B}
              onChange={(e) => setTipos((t) => ({ ...t, B: e.target.checked }))} /><LayoutGrid className="w-4 h-4 text-slate-500" /> Documento B (análisis)</label>
            <label className="flex items-center gap-2 text-sm" title="El informe del ERP con enero–mayo de 2026 restituido, anterior al cambio de ponderación. Solo para 2026 de junio en adelante y para los comerciales con cifras congeladas de mayo.">
              <input type="checkbox" checked={tipos.C}
                onChange={(e) => setTipos((t) => ({ ...t, C: e.target.checked }))} /><History className="w-4 h-4 text-slate-500" /> Documento C (ene–may restituido)</label>
            <Button onClick={generar} disabled={generando} className="gap-2 ml-auto">
              {generando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBarChart className="w-4 h-4" />}
              Generar {seleccion.size ? `(${seleccion.size})` : ''}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

function GenerarGrupos({ familia, onDone }) {
  const [files, setFiles] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [job, setJob] = useState(null);          // {job_id, items:[{key,grupo,periodo_label,con_deposito}]}
  const [modelos, setModelos] = useState({ A: true, B: true });
  const [generando, setGenerando] = useState(false);
  const esDepositos = familia === 'grupos_depositos';

  const subir = async () => {
    if (!files.length) return toast.error('Añade al menos un PDF de grupo.');
    setSubiendo(true);
    try {
      const r = await informesApi.gruposSubir(files);
      setJob(r);
    } catch (e) { toast.error(e.message); } finally { setSubiendo(false); }
  };

  const setGrupo = (key, val) => setJob((j) => ({
    ...j, items: j.items.map((it) => (it.key === key ? { ...it, grupo: val } : it)),
  }));

  const generar = async () => {
    const modelosSel = Object.entries(modelos).filter(([, v]) => v).map(([k]) => k);
    if (!modelosSel.length) return toast.error('Selecciona modelo A y/o B.');
    setGenerando(true);
    try {
      const items = job.items.map(({ key, grupo }) => ({ key, grupo }));
      const r = await informesApi.gruposGenerar(job.job_id, familia, modelosSel, items);
      toast.success(`${r.total} informes generados.`);
      (r.avisos || []).forEach((a) => toast.warning(a));
      onDone();
    } catch (e) { toast.error(e.message); } finally { setGenerando(false); }
  };

  return (
    <div className="space-y-5">
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-sky-700">
          1 · Subir PDF (uno por grupo{esDepositos ? ', con depósitos' : ''})
        </h2>
        <label className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg p-4 cursor-pointer hover:border-sky-400">
          <Upload className="w-5 h-5 text-slate-400" />
          <span className="text-sm text-slate-600">
            {files.length ? `${files.length} PDF seleccionados` : 'Selecciona los PDF de los grupos…'}
          </span>
          <input type="file" accept="application/pdf" multiple className="hidden"
            onChange={(e) => setFiles([...(e.target.files || [])])} />
        </label>
        <Button onClick={subir} disabled={subiendo} className="gap-2">
          {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Analizar
        </Button>
      </section>

      {job && (
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-sky-700">2 · Revisar grupos y generar</h2>
          <p className="text-xs text-slate-500">El Documento A guarda el PDF tal cual. Ajusta el nombre del grupo si hace falta.</p>
          <div className="overflow-x-auto border border-slate-100 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr><th className="p-2 text-left">Fichero</th><th className="p-2 text-left">Grupo</th>
                  <th className="p-2 text-left">Periodo</th><th className="p-2 text-center">Depósitos</th></tr>
              </thead>
              <tbody>
                {job.items.map((it) => (
                  <tr key={it.key} className="border-t border-slate-100">
                    <td className="p-2 text-slate-500 text-xs">{it.fichero}</td>
                    <td className="p-2">
                      <input value={it.grupo} onChange={(e) => setGrupo(it.key, e.target.value)}
                        className="text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1 w-40" />
                    </td>
                    <td className="p-2 text-slate-500">{it.periodo_label || '—'}</td>
                    <td className="p-2 text-center">{it.con_deposito
                      ? <span className="text-sky-600 text-xs font-semibold">sí</span>
                      : <span className="text-slate-400 text-xs">no</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={modelos.A}
              onChange={(e) => setModelos((m) => ({ ...m, A: e.target.checked }))} /><FileText className="w-4 h-4 text-slate-500" /> Modelo A (PDF fiel)</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={modelos.B}
              onChange={(e) => setModelos((m) => ({ ...m, B: e.target.checked }))} /><LayoutGrid className="w-4 h-4 text-slate-500" /> Modelo B (análisis)</label>
            <Button onClick={generar} disabled={generando} className="gap-2 ml-auto">
              {generando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBarChart className="w-4 h-4" />}
              Generar {job.items.length}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

/* ───────────────────────── BIBLIOTECA ───────────────────────── */

function Biblioteca({ gestor, puedeBorrar, puedeEditar }) {
  const [items, setItems] = useState([]);
  const [facetas, setFacetas] = useState({ periodos: [], zonas: [], comerciales: [], grupos: [], familias: [], tipos: [] });
  const [usuarios, setUsuarios] = useState([]);
  const [filtros, setFiltros] = useState({ familia: '', grupo: '', periodo: '', zona: '', comercial_codigo: '', tipo: '', estado: '', q: '' });
  const [cargando, setCargando] = useState(true);
  const [sel, setSel] = useState(() => new Set());
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
      const r = await informesApi.list(params);
      setItems(r.items || []);
      setSel(new Set());
    } catch (e) { toast.error(e.message); } finally { setCargando(false); }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    informesApi.facetas().then(setFacetas).catch(() => {});
    if (gestor) usuariosApi.seleccionables().then(setUsuarios).catch(() => {});
  }, [gestor]);

  // Agrupar por periodo (ya vienen ordenados desc por periodo)
  const grupos = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const k = it.periodo || '—';
      if (!m.has(k)) m.set(k, { label: it.periodo_label || k, items: [] });
      m.get(k).items.push(it);
    }
    return [...m.entries()];
  }, [items]);

  const setF = (k, v) => setFiltros((f) => ({ ...f, [k]: v }));

  // Selección múltiple (por unidad = conjunto de ids A+B)
  const toggleSelMuchos = (ids, marcar) => setSel((p) => {
    const s = new Set(p); ids.forEach((id) => (marcar ? s.add(id) : s.delete(id))); return s;
  });
  const limpiarSel = () => setSel(new Set());

  const descargar = async (it, formato) => {
    try {
      const blob = await informesApi.descargar(it.id, formato);
      descargarBlob(blob, `${it.nombre_descarga || `Informe_${it.comercial_codigo}`}.${formato}`);
    } catch (e) { toast.error(e.message); }
  };

  const ver = async (it, formato) => {
    try {
      const blob = await informesApi.descargar(it.id, formato);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');   // PDF/HTML se abren inline en pestaña nueva
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.message); }
  };

  // Acciones sobre la selección
  const zipSeleccion = async () => {
    setOcupado(true);
    try {
      const blob = await informesApi.zip({ ids: [...sel] });
      descargarBlob(blob, 'Informes_seleccion.zip');
    } catch (e) { toast.error(e.message); } finally { setOcupado(false); }
  };
  const publicarSeleccion = async (publicado) => {
    setOcupado(true);
    try {
      const r = await informesApi.publicarMasivo({ ids: [...sel], publicado });
      toast.success(`${r.actualizados} informes ${publicado ? 'publicados' : 'ocultados'}.`);
      cargar();
    } catch (e) { toast.error(e.message); } finally { setOcupado(false); }
  };
  const borrarSeleccion = async () => {
    if (!window.confirm(`¿Borrar ${sel.size} informes seleccionados?`)) return;
    setOcupado(true);
    try {
      const r = await informesApi.borrarMasivo({ ids: [...sel] });
      toast.success(`${r.borrados} informes borrados.`);
      cargar();
    } catch (e) { toast.error(e.message); } finally { setOcupado(false); }
  };

  // Acciones a nivel de periodo
  const zipPeriodo = async (periodo, label) => {
    try {
      const blob = await informesApi.zip({ periodo });
      descargarBlob(blob, `Informes_${label.replace(/\s+/g, '_')}.zip`);
    } catch (e) { toast.error(e.message); }
  };
  const publicarPeriodo = async (periodo, publicado) => {
    try {
      const r = await informesApi.publicarMasivo({ periodo, publicado });
      toast.success(`${r.actualizados} informes ${publicado ? 'publicados' : 'ocultados'}.`);
      cargar();
    } catch (e) { toast.error(e.message); }
  };

  // Acciones a nivel de UNIDAD (A+B juntos): reciben la lista de ids de la unidad
  const publicarUnidad = async (ids, publicado) => {
    try {
      await informesApi.publicarMasivo({ ids, publicado });
      setItems((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, publicado } : x)));
    } catch (e) { toast.error(e.message); }
  };

  const reasignarUnidad = async (ids, uid) => {
    try {
      await Promise.all(ids.map((id) => informesApi.asignar(id, uid || null)));
      setItems((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, usuario_destinatario: uid || null } : x)));
      toast.success('Destinatario actualizado.');
    } catch (e) { toast.error(e.message); }
  };

  const borrarUnidad = async (ids, nombre) => {
    if (!window.confirm(`¿Borrar el informe de ${nombre} (A y B)?`)) return;
    try {
      await informesApi.borrarMasivo({ ids });
      setItems((prev) => prev.filter((x) => !ids.includes(x.id)));
    } catch (e) { toast.error(e.message); }
  };

  const borrarPeriodo = async (periodo, label) => {
    if (!window.confirm(`¿Borrar TODOS los informes de ${label}?`)) return;
    try {
      const r = await informesApi.borrarMasivo({ periodo });
      toast.success(`${r.borrados} informes borrados.`);
      cargar();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl">
      {/* Toolbar de filtros */}
      <div className="flex flex-wrap gap-2 items-end p-4 border-b border-slate-100">
        <Filtro label="Familia" value={filtros.familia} onChange={(v) => setF('familia', v)}
          options={[['', 'Todas'], ...FAMILIAS.map((f) => [f.clave, f.label])]} />
        {(facetas.grupos || []).length > 0 && (
          <Filtro label="Grupo" value={filtros.grupo} onChange={(v) => setF('grupo', v)}
            options={[['', 'Todos'], ...facetas.grupos.map((g) => [g, g])]} />
        )}
        <Filtro label="Periodo" value={filtros.periodo} onChange={(v) => setF('periodo', v)}
          options={[['', 'Todos'], ...facetas.periodos.map((p) => [p.periodo, p.label])]} />
        {(facetas.zonas || []).length > 0 && (
          <Filtro label="Zona" value={filtros.zona} onChange={(v) => setF('zona', v)}
            options={[['', 'Todas'], ...facetas.zonas.map((z) => [z, z])]} />
        )}
        {gestor && (
          <Filtro label="Comercial" value={filtros.comercial_codigo} onChange={(v) => setF('comercial_codigo', v)}
            options={[['', 'Todos'], ...facetas.comerciales.map((c) => [c.codigo, c.nombre])]} />
        )}
        <Filtro label="Tipo" value={filtros.tipo} onChange={(v) => setF('tipo', v)}
          options={[['', 'Todos'], ...facetas.tipos.map((t) => [t.tipo, t.label])]} />
        {gestor && (
          <Filtro label="Estado" value={filtros.estado} onChange={(v) => setF('estado', v)}
            options={[['', 'Todos'], ['publicado', 'Publicado'], ['borrador', 'Borrador']]} />
        )}
        <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 pl-0.5">Buscar</label>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
            <input value={filtros.q} onChange={(e) => setF('q', e.target.value)} placeholder="zona, comercial, código…"
              className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-2" />
          </div>
        </div>
      </div>

      {/* Barra de acciones masivas */}
      {gestor && sel.size > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-sky-50 border-b border-sky-100 flex-wrap sticky top-0 z-10">
          <span className="text-sm font-semibold text-sky-800">{sel.size} seleccionados</span>
          <button onClick={limpiarSel} className="text-slate-400 hover:text-slate-700 p-1" title="Limpiar selección"><X className="w-4 h-4" /></button>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" disabled={ocupado} onClick={zipSeleccion} className="gap-1"><Package className="w-4 h-4" /> ZIP</Button>
          {puedeEditar && <Button variant="ghost" size="sm" disabled={ocupado} onClick={() => publicarSeleccion(true)} className="gap-1"><Eye className="w-4 h-4" /> Publicar</Button>}
          {puedeEditar && <Button variant="ghost" size="sm" disabled={ocupado} onClick={() => publicarSeleccion(false)} className="gap-1"><EyeOff className="w-4 h-4" /> Ocultar</Button>}
          {puedeBorrar && <Button variant="ghost" size="sm" disabled={ocupado} onClick={borrarSeleccion} className="gap-1 text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /> Borrar</Button>}
        </div>
      )}

      {cargando ? (
        <div className="p-6 text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : grupos.length === 0 ? (
        <p className="p-6 text-slate-400 text-sm">No hay informes con estos filtros.</p>
      ) : grupos.map(([periodo, g]) => (
        <PeriodoGrupo key={periodo} periodo={periodo} label={g.label} items={g.items}
          gestor={gestor} puedeBorrar={puedeBorrar} puedeEditar={puedeEditar} usuarios={usuarios}
          sel={sel} onToggleSelMuchos={toggleSelMuchos}
          onDescargar={descargar} onVer={ver}
          onPublicarUnidad={publicarUnidad} onReasignarUnidad={reasignarUnidad} onBorrarUnidad={borrarUnidad}
          onBorrarPeriodo={() => borrarPeriodo(periodo, g.label)}
          onZipPeriodo={() => zipPeriodo(periodo, g.label)}
          onPublicarPeriodo={(pub) => publicarPeriodo(periodo, pub)} />
      ))}
    </section>
  );
}

// Agrupa los informes (docs A/B) de un periodo en UNIDADES (grupo/comercial),
// de modo que A y B aparezcan en una sola fila con sus 3 descargas.
function agruparUnidades(items) {
  const m = new Map();
  const orden = ['A', 'B'];
  for (const it of items) {
    const clave = `${it.familia || 'zona'}|${it.comercial_codigo || it.grupo || it.id}`;
    if (!m.has(clave)) m.set(clave, { clave, rep: it, docs: [] });
    m.get(clave).docs.push(it);
  }
  return [...m.values()].map((u) => {
    const docs = u.docs.slice().sort((a, b) => orden.indexOf(a.tipo) - orden.indexOf(b.tipo));
    return {
      clave: u.clave,
      rep: u.rep,
      docs,
      ids: docs.map((d) => d.id),
      publicado: docs.every((d) => d.publicado),
      // botones: [{doc, fmt}] por cada formato de cada modelo (A·PDF, B·PDF, B·HTML)
      botones: docs.flatMap((d) => Object.keys(d.formatos || {}).sort().map((fmt) => ({ doc: d, fmt }))),
    };
  });
}

function PeriodoGrupo({ periodo, label, items, gestor, puedeBorrar, puedeEditar, usuarios,
  sel, onToggleSelMuchos,
  onDescargar, onVer, onPublicarUnidad, onReasignarUnidad, onBorrarUnidad,
  onBorrarPeriodo, onZipPeriodo, onPublicarPeriodo }) {
  const [open, setOpen] = useState(true);
  const [verAvisos, setVerAvisos] = useState(null);   // clave de la unidad desplegada
  const unidades = agruparUnidades(items);
  const publicados = unidades.filter((u) => u.publicado).length;
  const sinAsignar = unidades.filter((u) => u.rep.familia === 'zona' && !u.rep.usuario_destinatario).length;
  const allIds = items.map((i) => i.id);
  const todosSel = allIds.length > 0 && allIds.every((id) => sel.has(id));

  return (
    <div className="border-t border-slate-100 first:border-t-0">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => !o)}>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
        <span className="font-semibold text-slate-800">{label}</span>
        <span className="text-xs text-slate-400">
          · {unidades.length} informes{gestor ? ` · ${publicados} publicados` : ''}{gestor && sinAsignar ? ` · ${sinAsignar} sin asignar` : ''}
        </span>
        <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button className="text-slate-400 hover:text-sky-600 p-1.5" title={`Descargar ${label} en ZIP`} onClick={onZipPeriodo}>
            <Package className="w-4 h-4" />
          </button>
          {gestor && puedeEditar && (
            <button className="text-xs font-semibold text-slate-500 hover:text-emerald-600 px-2 py-1" title={`Publicar todo ${label}`}
              onClick={() => onPublicarPeriodo(true)}>Publicar lote</button>
          )}
          {gestor && puedeBorrar && (
            <button className="text-slate-400 hover:text-red-600 p-1.5" title={`Borrar todo ${label}`} onClick={onBorrarPeriodo}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                {gestor && <th className="p-2 w-8 text-left"><input type="checkbox" checked={todosSel}
                  onChange={(e) => onToggleSelMuchos(allIds, e.target.checked)} aria-label="Seleccionar periodo" /></th>}
                <th className="p-2 text-left">Docs</th>
                <th className="p-2 text-left">Zona / grupo</th>
                {gestor && <th className="p-2 text-left">Comercial · destinatario</th>}
                {gestor && <th className="p-2 text-center">Publicado</th>}
                <th className="p-2 text-right">Descargas</th>
              </tr>
            </thead>
            <tbody>
              {unidades.map((u) => {
                const it = u.rep;
                const selUnidad = u.ids.every((id) => sel.has(id));
                const esGrupo = it.familia && it.familia !== 'zona';
                const avisos = it.cuadre_avisos || [];
                const avisosAbiertos = verAvisos === u.clave;
                return (
                  <React.Fragment key={u.clave}>
                  <tr className={`border-t border-slate-100 hover:bg-slate-50 ${selUnidad ? 'bg-sky-50/60' : ''}`}>
                    {gestor && <td className="p-2"><input type="checkbox" checked={selUnidad}
                      onChange={(e) => onToggleSelMuchos(u.ids, e.target.checked)} aria-label="sel" /></td>}
                    <td className="p-2">
                      <span className="inline-flex gap-1">
                        {u.docs.map((d) => (
                          <span key={d.tipo} className={`px-2 py-0.5 rounded text-xs font-semibold ${COLOR_TIPO[d.tipo] || COLOR_TIPO._}`}>{d.tipo}</span>
                        ))}
                      </span>
                    </td>
                    <td className="p-2">
                      {esGrupo
                        ? <span>{it.grupo} <span className="text-[10px] uppercase tracking-wide text-slate-400 ml-1">{it.familia === 'grupos_depositos' ? 'depósitos' : 'grupo'}</span></span>
                        : (it.zona_crm || <span className="text-slate-400">{it.comercial_nombre}</span>)}
                      {avisos.length > 0 && (
                        <button type="button" onClick={() => setVerAvisos(avisosAbiertos ? null : u.clave)}
                          title="Ver las comprobaciones que no cuadran"
                          className={`ml-2 inline-flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 align-middle
                            border transition-colors ${avisosAbiertos
                              ? 'bg-amber-500 border-amber-500 text-white'
                              : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'}`}>
                          <AlertTriangle className="w-3 h-3" /> {avisos.length}
                          <ChevronDown className={`w-3 h-3 transition-transform ${avisosAbiertos ? '' : '-rotate-90'}`} />
                        </button>
                      )}
                    </td>
                    {gestor && (
                      <td className="p-2">
                        {esGrupo ? (
                          <span className="text-slate-300 text-xs">—</span>
                        ) : puedeEditar ? (
                          <select value={it.usuario_destinatario || ''} onChange={(e) => onReasignarUnidad(u.ids, e.target.value)}
                            className={`text-xs bg-slate-50 border rounded px-2 py-1 ${it.usuario_destinatario ? 'border-slate-200' : 'border-amber-300 text-amber-700'}`}>
                            <option value="">— sin asignar —</option>
                            {usuarios.map((usr) => <option key={usr.id} value={usr.id}>{usr.nombre_completo}</option>)}
                          </select>
                        ) : (it.usuario_destinatario || '—')}
                      </td>
                    )}
                    {gestor && (
                      <td className="p-2 text-center">
                        <button onClick={() => onPublicarUnidad(u.ids, !u.publicado)} title={u.publicado ? 'Publicado — clic para ocultar' : 'Borrador — clic para publicar'}>
                          {u.publicado ? <Eye className="w-4 h-4 text-emerald-600 inline" /> : <EyeOff className="w-4 h-4 text-slate-400 inline" />}
                        </button>
                      </td>
                    )}
                    <td className="p-2">
                      <div className="flex gap-1 justify-end items-center flex-wrap">
                        {u.botones.map(({ doc, fmt }) => (
                          <span key={`${doc.tipo}-${fmt}`} className="inline-flex rounded overflow-hidden border border-slate-200">
                            <button onClick={() => onVer(doc, fmt)} title="Ver sin descargar"
                              className="text-xs font-semibold px-2 py-1 text-slate-600 hover:bg-sky-50 hover:text-sky-600">
                              {doc.tipo}·{fmt.toUpperCase()}
                            </button>
                            <button onClick={() => onDescargar(doc, fmt)} title="Descargar"
                              className="px-1.5 py-1 border-l border-slate-200 text-slate-400 hover:bg-sky-50 hover:text-sky-600">
                              <Download className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                        {puedeBorrar && (
                          <button onClick={() => onBorrarUnidad(u.ids, esGrupo ? it.grupo : (it.zona_crm || it.comercial_nombre))} title="Borrar informe (A y B)"
                            className="text-xs border border-slate-200 rounded px-1.5 py-1 text-slate-400 hover:border-red-500 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {avisosAbiertos && (
                    <tr className="bg-amber-50/60 border-t border-amber-100">
                      <td colSpan={gestor ? 6 : 3} className="px-4 py-3">
                        <p className="text-xs font-semibold text-amber-800 mb-1.5">
                          Comprobaciones que no cuadraban al generar este informe
                        </p>
                        <ul className="space-y-1 text-xs text-amber-900">
                          {avisos.map((a, i) => (
                            <li key={i} className="flex gap-1.5">
                              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {a}
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-amber-700/80 mt-2">
                          Los datos vienen del ERP: corrígelo allí, borra este informe y vuelve a subirlo.
                        </p>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── AJUSTES ───────────────────────── */

function Ajustes() {
  const [rows, setRows] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [c, u] = await Promise.all([comisionistasApi.list(), usuariosApi.seleccionables()]);
      setRows(c.items || []);
      setUsuarios(u || []);
    } catch (e) { toast.error(e.message); } finally { setCargando(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const vincular = async (codigo, uid) => {
    try {
      if (uid) await comisionistasApi.vincular(codigo, uid);
      else await comisionistasApi.desvincular(codigo);
      setRows((prev) => prev.map((r) => r.codigo_erp === codigo
        ? { ...r, usuario_id: uid || null, usuario_nombre: uid ? (usuarios.find((x) => x.id === uid)?.nombre_completo || '') : null, vinculado: !!uid }
        : r));
      toast.success(uid ? 'Destinatario asignado.' : 'Destinatario retirado.');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl">
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center gap-2 text-sky-700"><Settings className="w-4 h-4" />
          <h2 className="text-sm font-semibold uppercase tracking-wide">Destinatario por comercial</h2></div>
        <p className="text-xs text-slate-500 mt-1">
          Enlace comercial (código ERP) → usuario que recibe sus informes. Tiene prioridad sobre el
          titular de zona automático y se aplica en las próximas generaciones. Vacío = se usa el titular de la zona.
        </p>
      </div>
      {cargando ? (
        <div className="p-6 text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-2 text-left">Comercial (ERP)</th>
                <th className="p-2 text-left">Destinatario</th>
                <th className="p-2 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.codigo_erp} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-2"><span className="text-slate-400">{r.codigo_erp}</span> {r.nombre_erp}</td>
                  <td className="p-2">
                    <select value={r.usuario_id || ''} onChange={(e) => vincular(r.codigo_erp, e.target.value)}
                      className={`text-xs bg-slate-50 border rounded px-2 py-1 min-w-[180px] ${r.usuario_id ? 'border-slate-200' : 'border-slate-200 text-slate-400'}`}>
                      <option value="">— usar titular de zona —</option>
                      {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre_completo}</option>)}
                    </select>
                  </td>
                  <td className="p-2 text-center">
                    {r.usuario_id
                      ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Link2 className="w-3.5 h-3.5" /> enlazado</span>
                      : <span className="text-slate-400 text-xs">automático</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ───────────────────────── UI helpers ───────────────────────── */

function Filtro({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 pl-0.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="text-sm bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 min-w-[120px]">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function FileDrop({ label, file, onChange }) {
  return (
    <label className="flex flex-col gap-1 cursor-pointer">
      <span className="text-xs text-slate-500">{label}</span>
      <div className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg p-3 hover:border-sky-400 transition">
        <FileText className="w-5 h-5 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-600 truncate">{file ? file.name : 'Seleccionar PDF…'}</span>
        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </div>
    </label>
  );
}
